import React, { useState, useEffect } from 'react';
import { Save, Loader2, DollarSign, TrendingUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { financialAPI } from '../../services/api';

const FinancialManagement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [financialData, setFinancialData] = useState({
    revenue: [
      { month: 'Jan', value: 0 },
      { month: 'Fév', value: 0 },
      { month: 'Mar', value: 0 },
      { month: 'Avr', value: 0 },
      { month: 'Mai', value: 0 },
      { month: 'Jun', value: 0 },
      { month: 'Jul', value: 0 },
      { month: 'Aoû', value: 0 },
      { month: 'Sep', value: 0 },
      { month: 'Oct', value: 0 },
      { month: 'Nov', value: 0 },
      { month: 'Déc', value: 0 }
    ],
    expenses: {},
    profit_margin: 0
  });

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    setLoading(true);
    try {
      const response = await financialAPI.get();
      if (response.data) {
        setFinancialData(response.data);
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
      toast.error('Erreur lors du chargement des données financières');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (data) => {
    if (!data || !data.revenue || !data.expenses) {
      return { totalRevenue: 0, totalExpenses: 0, profit: 0, profitMargin: 0, roi: 0 };
    }
    const totalRevenue = data.revenue.reduce((sum, month) => sum + parseFloat(month.value || 0), 0);
    const totalExpenses = Object.values(data.expenses).reduce((sum, val) => sum + parseFloat(val || 0), 0);
    const profit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(2) : 0;
    const roi = totalExpenses > 0 ? ((profit / totalExpenses) * 100).toFixed(2) : 0;
    
    return { totalRevenue, totalExpenses, profit, profitMargin, roi };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const metrics = calculateMetrics(financialData);
      
      const dataToSave = {
        ...financialData,
        profit_margin: parseFloat(metrics.profitMargin)
      };
      
      await financialAPI.update(dataToSave);
      toast.success('Données financières mises à jour');
      loadFinancialData();
    } catch (error) {
      console.error('Error saving financial data:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRevenueChange = (index, value) => {
    const newRevenue = [...financialData.revenue];
    newRevenue[index].value = parseFloat(value) || 0;
    setFinancialData(prev => ({ ...prev, revenue: newRevenue }));
  };

  const handleExpenseChange = (category, value) => {
    setFinancialData(prev => ({
      ...prev,
      expenses: { ...prev.expenses, [category]: parseFloat(value) || 0 }
    }));
  };

  const addExpenseCategory = () => {
    const category = prompt('Nom de la catégorie de dépense :');
    if (category && category.trim()) {
      setFinancialData(prev => ({
        ...prev,
        expenses: { ...prev.expenses, [category.trim()]: 0 }
      }));
    }
  };

  const removeExpenseCategory = (category) => {
    if (window.confirm(`Supprimer la catégorie "${category}" ?`)) {
      const newExpenses = { ...financialData.expenses };
      delete newExpenses[category];
      setFinancialData(prev => ({ ...prev, expenses: newExpenses }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const metrics = calculateMetrics(financialData);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <DollarSign className="w-6 h-6" />
          Gestion Financière
        </h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </>
          )}
        </Button>
      </div>

      {/* Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Revenus Totaux</p>
          <p className="text-2xl font-bold text-white mt-1">
            {metrics.totalRevenue.toLocaleString()} €
          </p>
        </div>
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Dépenses Totales</p>
          <p className="text-2xl font-bold text-white mt-1">
            {metrics.totalExpenses.toLocaleString()} €
          </p>
        </div>
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Profit</p>
          <p className="text-2xl font-bold text-white mt-1">
            {metrics.profit.toLocaleString()} €
          </p>
        </div>
        <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Marge Bénéficiaire</p>
          <p className="text-2xl font-bold text-white mt-1">
            {metrics.profitMargin}%
          </p>
        </div>
      </div>

      {/* Monthly Revenue */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Revenus Mensuels
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {financialData.revenue.map((month, index) => (
            <div key={month.month}>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {month.month}
              </label>
              <Input
                type="number"
                value={month.value}
                onChange={(e) => handleRevenueChange(index, e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="0"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Expenses */}
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-white">Dépenses par Catégorie</h3>
          <Button
            onClick={addExpenseCategory}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter Catégorie
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(financialData.expenses).map(([category, value]) => (
            <div key={category} className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {category}
                </label>
                <Input
                  type="number"
                  value={value}
                  onChange={(e) => handleExpenseChange(category, e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="0"
                />
              </div>
              <Button
                onClick={() => removeExpenseCategory(category)}
                size="sm"
                variant="destructive"
                className="mt-6"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        {Object.keys(financialData.expenses).length === 0 && (
          <p className="text-gray-400 text-center py-4">
            Aucune catégorie de dépense. Cliquez sur "Ajouter Catégorie" pour commencer.
          </p>
        )}
      </div>

      {/* Additional Metrics */}
      <div className="glass rounded-xl p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Métriques Avancées</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-700/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">ROI (Return on Investment)</p>
            <p className="text-3xl font-bold text-green-400">{metrics.roi}%</p>
            <p className="text-xs text-gray-500 mt-1">
              Calculé automatiquement : (Profit / Dépenses) × 100
            </p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Cash Flow</p>
            <p className={`text-3xl font-bold ${metrics.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {metrics.profit >= 0 ? '+' : ''}{metrics.profit.toLocaleString()} €
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Revenus - Dépenses
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialManagement;
